/**
 * OrgMembershipContext — Phase 4 unified auth.
 *
 * Auth resolution uses ONLY the normal user session (session_token cookie).
 * Organization access comes exclusively from organization_memberships.
 * No CM session cookie, no legacy changemaker_session_token.
 *
 * Exports:
 *   OrgMembershipProvider   — new provider name
 *   useOrgMembership        — new hook name
 *   ChangemakerAuthProvider — alias (backward compat for App.js)
 *   useChangemakerAuth      — alias (backward compat for existing consumers)
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getMyMemberships } from '../api/api';
import { useAuth } from './AuthContext';

const OrgMembershipContext = createContext(null);

export function OrgMembershipProvider({ children }) {
  const { user, logout: authLogout } = useAuth();
  const [membership, setMembership] = useState(null);   // { membership: {...}, organization: {...} }
  const [loading, setLoading]       = useState(false);
  const [isChangemakerMode, setIsChangemakerMode] = useState(false);

  const fetchMemberships = useCallback(async () => {
    if (!user) {
      setMembership(null);
      setIsChangemakerMode(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await getMyMemberships();
      const first = Array.isArray(data) && data.length > 0 ? data[0] : null;
      setMembership(first);
      if (first) setIsChangemakerMode(true);
    } catch {
      setMembership(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchMemberships(); }, [fetchMemberships]);

  // Single logout — uses only the normal Karmana auth
  const logout = async () => {
    try { await authLogout(); } catch { /* silent */ }
    setMembership(null);
    setIsChangemakerMode(false);
  };

  // Backward-compat: login() is no longer needed (auth happens via AuthContext)
  const login = () => {};

  const toggleMode = () => setIsChangemakerMode((prev) => !prev);

  // ── Backward-compat mappings for existing consumers ────────────────────────
  const org    = membership?.organization || null;
  const cmUser = user && org ? { ...user, changemaker_id: org.id } : null;
  const cmOrg  = org;
  const cmRole = (() => {
    const raw = membership?.membership?.role || 'admin';
    // Normalise legacy "changemaker_admin" → "admin"
    return raw === 'changemaker_admin' ? 'admin' : raw;
  })();

  return (
    <OrgMembershipContext.Provider value={{
      cmUser, cmOrg, cmRole, loading,
      login, logout, refreshMe: fetchMemberships,
      isChangemakerMode, toggleMode,
      // New names (preferred going forward)
      membership, isOrgMode: isChangemakerMode,
    }}>
      {children}
    </OrgMembershipContext.Provider>
  );
}

export const useOrgMembership = () => useContext(OrgMembershipContext);

// ── Backward-compat aliases ───────────────────────────────────────────────────
export const ChangemakerAuthProvider = OrgMembershipProvider;
export const useChangemakerAuth = useOrgMembership;
